import React, { useEffect, useState } from 'react';
import { Zap, ShieldCheck, Gift, LayoutDashboard } from 'lucide-react';
import SportsbookLogo from './SportsbookLogo';
import TeamLogo from './TeamLogo';

const TOOLS = [
  {
    className: 'browse',
    icon: LayoutDashboard,
    title: 'Odds Dashboard',
    tagline: 'Every line, every book',
    description:
      'Compare live odds from all major U.S. sportsbooks side-by-side. Best available price is highlighted automatically. Build parlays from any game.',
  },
  {
    className: 'ev',
    icon: Zap,
    title: '+EV Finder',
    tagline: 'Beat the books long-term',
    description:
      'Identifies bets where the odds are mathematically in your favor. Based on sharp market consensus — place enough of these and the edge compounds over time.',
  },
  {
    className: 'arb',
    icon: ShieldCheck,
    title: 'Arbitrage Finder',
    tagline: 'Guaranteed profit, no risk',
    description:
      "When sportsbooks disagree enough, you can bet both sides and lock in a return no matter who wins. We find these windows and calculate the exact stake split.",
  },
  {
    className: 'promo',
    icon: Gift,
    title: 'Promo Converter',
    tagline: 'Turn promos into real cash',
    description:
      'Free bets and profit boosts can be hedged into guaranteed, withdrawable cash. Enter the promo details and we calculate the optimal hedge amounts.',
  },
];

const STEPS = [
  {
    number: '01',
    title: 'We track it',
    body: 'Live odds from the biggest U.S. sportsbooks, pulled and compared in real time as lines move.',
  },
  {
    number: '02',
    title: 'Math finds it',
    body: 'Algorithms flag every +EV bet, arbitrage window, and promo opportunity the moment it appears.',
  },
  {
    number: '03',
    title: 'You place it',
    body: 'We tell you exactly which book, which bet, and how much to stake. You execute — in under a minute.',
  },
];

const BOOKS = [
  { name: 'DraftKings', color: '#4ade80', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.28)' },
  { name: 'FanDuel',    color: '#60a5fa', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.28)' },
  { name: 'BetMGM',     color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.28)' },
  { name: 'Caesars',    color: '#c084fc', bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.28)' },
  { name: 'theScore Bet', color: '#f87171', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.28)'  },
  { name: 'BetRivers',  color: '#fb923c', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.28)' },
  { name: 'Fanatics',   color: '#f472b6', bg: 'rgba(236,72,153,0.12)', border: 'rgba(236,72,153,0.28)' },
  { name: 'Bally Bet',  color: '#22d3ee', bg: 'rgba(34,211,238,0.12)', border: 'rgba(34,211,238,0.28)' },
];

const FAQS = [
  {
    q: 'Is this legal?',
    a: 'Yes. Arbitrage betting and +EV betting are completely legal in every U.S. state where sports betting is permitted. You\'re placing normal bets at fully licensed sportsbooks — NavBets just helps you find the best prices.',
  },
  {
    q: 'How much can I actually make?',
    a: 'Arbitrage opportunities typically yield 0.5–3% profit per bet. With a $2,000 bankroll and consistent execution, that translates to a few hundred dollars a month. +EV betting compounds over hundreds of bets — the edge is smaller per bet but adds up significantly over time.',
  },
  {
    q: 'Do I need a large bankroll to start?',
    a: 'No. You can start with as little as $500 across two or three sportsbooks. NavBets calculates the optimal stake split for your exact bankroll, so every arb is sized correctly no matter how much you\'re working with.',
  },
  {
    q: 'Will sportsbooks limit or ban me?',
    a: 'Sportsbooks can restrict sharp bettors over time — this is a real consideration. Most successful arb bettors spread action across many books, avoid placing maximum bets early, and use promo tools to stay under the radar. NavBets tracks 8+ books to give you the most options.',
  },
  {
    q: 'What\'s the difference between arbitrage and +EV betting?',
    a: 'Arbitrage locks in a guaranteed profit by betting both sides of the same event at different sportsbooks — no matter who wins, you profit. +EV betting is a single-book bet where the odds are mathematically in your favor based on the sharp market consensus. Arb is risk-free; +EV relies on volume for the edge to play out.',
  },
];

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' faq-item--open' : ''}`} onClick={() => setOpen(o => !o)}>
      <div className="faq-question">
        <span>{faq.q}</span>
        <span className="faq-chevron">{open ? '−' : '+'}</span>
      </div>
      {open && <p className="faq-answer">{faq.a}</p>}
    </div>
  );
}

function fmtOdds(odds) {
  if (odds == null) return '—';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function fmtGameTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function cleanTeamName(name) {
  return (name || '').replace(/\s*\([^)]+\)\s*$/, '').trim();
}

function marketLabel(market) {
  if (market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Totals';
  return market || '';
}

function PreviewArbCard({ arb, onLoginRequest }) {
  if (!arb) return null;
  const profitPct = typeof arb.profit_percentage === 'number'
    ? arb.profit_percentage.toFixed(2)
    : '—';

  const isProp = arb.market === 'player_props';

  // For totals/spreads, actual_home/away_team has the real team name; home/away_team has the side label
  const displayHome = arb.actual_home_team || cleanTeamName(arb.home_team);
  const displayAway = arb.actual_away_team || cleanTeamName(arb.away_team);

  // Side fields differ between game arbs and prop arbs
  const sideAKey   = isProp ? arb.over_sportsbook_key  : arb.home_sportsbook_key;
  const sideBKey   = isProp ? arb.under_sportsbook_key : arb.away_sportsbook_key;
  const sideASb    = isProp ? arb.over_sportsbook       : arb.home_sportsbook;
  const sideBSb    = isProp ? arb.under_sportsbook      : arb.away_sportsbook;
  const sideAOdds  = isProp ? arb.over_odds             : arb.home_odds;
  const sideBOdds  = isProp ? arb.under_odds            : arb.away_odds;
  const sideALabel = isProp ? `Over ${arb.line}`        : arb.home_team;
  const sideBLabel = isProp ? `Under ${arb.line}`       : arb.away_team;

  const marketDisplay = isProp
    ? `${arb.prop_display || 'Prop'} Arbitrage`
    : `${marketLabel(arb.market)} Arbitrage`;

  return (
    <div className="preview-arb-card">
      <div className="preview-arb-header">
        <div className="preview-arb-meta">
          <span className="preview-arb-sport">{arb.sport_title}</span>
          <span className="preview-arb-time">{fmtGameTime(arb.commence_time)}</span>
        </div>
        <div className="preview-arb-header-right">
          {arb.is_live && <span className="preview-live-badge"><span className="preview-live-dot" />LIVE</span>}
          <span className="preview-arb-profit-badge">+{profitPct}% guaranteed</span>
        </div>
      </div>

      {isProp ? (
        <div className="preview-arb-teams">
          <div className="preview-arb-team-row">
            <TeamLogo teamName={displayHome} sportKey={arb.sport_key} size={24} />
            <TeamLogo teamName={displayAway} sportKey={arb.sport_key} size={24} />
            <span className="preview-arb-team-name">{displayHome} vs {displayAway}</span>
          </div>
          <div className="preview-arb-prop-player">
            <span className="preview-arb-player-name">{arb.player}</span>
            <span className="preview-arb-prop-line">· {arb.prop_display} {arb.line}</span>
          </div>
        </div>
      ) : (
        <div className="preview-arb-teams">
          <div className="preview-arb-team-row">
            <TeamLogo teamName={displayHome} sportKey={arb.sport_key} size={28} />
            <span className="preview-arb-team-name">{displayHome}</span>
            {arb.home_score != null && <span className="preview-score">{arb.home_score}</span>}
          </div>
          <span className="preview-arb-teams-vs">vs</span>
          <div className="preview-arb-team-row">
            <TeamLogo teamName={displayAway} sportKey={arb.sport_key} size={28} />
            <span className="preview-arb-team-name">{displayAway}</span>
            {arb.away_score != null && <span className="preview-score">{arb.away_score}</span>}
          </div>
        </div>
      )}
      <div className="preview-arb-market">{marketDisplay}</div>

      <div className="preview-arb-sides">
        <div className="preview-arb-side">
          <div className="preview-arb-book">
            <SportsbookLogo bookKey={sideAKey} size={24} />
            <span>{sideASb}</span>
          </div>
          <div className="preview-arb-side-label">{sideALabel}</div>
          <div className="preview-arb-odds">{fmtOdds(sideAOdds)}</div>
        </div>
        <div className="preview-arb-vs">VS</div>
        <div className="preview-arb-side">
          <div className="preview-arb-book">
            <SportsbookLogo bookKey={sideBKey} size={24} />
            <span>{sideBSb}</span>
          </div>
          <div className="preview-arb-side-label">{sideBLabel}</div>
          <div className="preview-arb-odds">{fmtOdds(sideBOdds)}</div>
        </div>
      </div>

      <button className="preview-arb-cta" onClick={onLoginRequest}>
        Sign up / Log in to see optimal bet amounts →
      </button>
    </div>
  );
}

function PreviewOddsCell({ point, odds, bookKey, bookTitle }) {
  return (
    <div className="preview-odds-cell">
      <div className="preview-odds-pill">
        {point != null && <span className="preview-odds-point">{point > 0 ? `+${point}` : point}</span>}
        <span className="preview-odds-price">{fmtOdds(odds)}</span>
      </div>
      <div className="preview-odds-book">
        <SportsbookLogo bookKey={bookKey} size={20} />
        <span>{bookTitle}</span>
      </div>
    </div>
  );
}

function PreviewOddsCard({ game, onLoginRequest }) {
  if (!game || !game.bookmakers || game.bookmakers.length === 0) return null;
  const books = game.bookmakers;

  const bestBy = (key) => books.reduce((b, c) => ((c[key] || -Infinity) > (b[key] || -Infinity) ? c : b), books[0]);

  const homeMLBook   = bestBy('home_odds');
  const awayMLBook   = bestBy('away_odds');
  const homeSprdBook = bestBy('home_spread_odds');
  const awaySprdBook = bestBy('away_spread_odds');
  const overBook     = bestBy('over_odds');
  const underBook    = bestBy('under_odds');

  const hasSpread = game.has_spreads && homeSprdBook.home_spread_odds != null;
  const hasTotal  = game.has_totals  && overBook.over_odds != null;
  const hasML     = homeMLBook.home_odds != null;

  return (
    <div className="preview-odds-card">
      <div className="preview-odds-header">
        <div className="preview-odds-sport-row">
          <span className="preview-odds-sport">{game.sport_title}</span>
          {game.is_live && <span className="preview-live-badge"><span className="preview-live-dot" />LIVE</span>}
        </div>
        <div className="preview-odds-cols">
          {hasSpread && <span className="preview-col-label">SPREAD</span>}
          {hasML     && <span className="preview-col-label">MONEY</span>}
          {hasTotal  && <span className="preview-col-label">TOTAL</span>}
        </div>
      </div>

      <div className="preview-odds-row">
        <div className="preview-odds-team">
          <TeamLogo teamName={game.away_team} sportKey={game.sport} size={24} />
          <span className="preview-team-name">{game.away_team}</span>
          {game.away_score != null && <span className="preview-score">{game.away_score}</span>}
        </div>
        <div className="preview-odds-cells">
          {hasSpread && (
            <PreviewOddsCell
              point={awaySprdBook.away_spread_point}
              odds={awaySprdBook.away_spread_odds}
              bookKey={awaySprdBook.name}
              bookTitle={awaySprdBook.title}
            />
          )}
          {hasML && (
            <PreviewOddsCell
              odds={awayMLBook.away_odds}
              bookKey={awayMLBook.name}
              bookTitle={awayMLBook.title}
            />
          )}
          {hasTotal && (
            <PreviewOddsCell
              point={underBook.under_point}
              odds={underBook.under_odds}
              bookKey={underBook.name}
              bookTitle={underBook.title}
              isUnder
            />
          )}
        </div>
      </div>

      <div className="preview-odds-divider"><span>@</span></div>

      <div className="preview-odds-row">
        <div className="preview-odds-team">
          <TeamLogo teamName={game.home_team} sportKey={game.sport} size={24} />
          <span className="preview-team-name">{game.home_team}</span>
          {game.home_score != null && <span className="preview-score">{game.home_score}</span>}
        </div>
        <div className="preview-odds-cells">
          {hasSpread && (
            <PreviewOddsCell
              point={homeSprdBook.home_spread_point}
              odds={homeSprdBook.home_spread_odds}
              bookKey={homeSprdBook.name}
              bookTitle={homeSprdBook.title}
            />
          )}
          {hasML && (
            <PreviewOddsCell
              odds={homeMLBook.home_odds}
              bookKey={homeMLBook.name}
              bookTitle={homeMLBook.title}
            />
          )}
          {hasTotal && (
            <PreviewOddsCell
              point={overBook.over_point}
              odds={overBook.over_odds}
              bookKey={overBook.name}
              bookTitle={overBook.title}
            />
          )}
        </div>
      </div>

      <div className="preview-odds-footer">
        <span className="preview-odds-gametime">{fmtGameTime(game.commence_time)}</span>
        <button className="preview-odds-cta" onClick={onLoginRequest}>
          Sign up / Log in to see all odds →
        </button>
      </div>
    </div>
  );
}

function LandingPage({ onLoginRequest, stats, onContact }) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(el => { if (el.isIntersecting) { el.target.classList.add('anim-visible'); observer.unobserve(el.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.anim').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const hasPreview = stats?.sampleArb || stats?.sampleGame;

  return (
    <div className="landing-page">

      {/* ── Nav ── */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-nav-brand">
            <img src="/navbets-logo-new.png" alt="NavBets" className="landing-logo" />
            <span className="landing-nav-wordmark">NavBets</span>
          </div>
          <button className="landing-nav-cta" onClick={onLoginRequest}>
            Sign Up / Log In
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <h1 className="landing-hero-title">
            Beat the sportsbooks.<br />
            <span className="landing-hero-accent">With math, not luck.</span>
          </h1>
          <p className="landing-hero-sub">
            Live odds from every major U.S. sportsbook, analyzed in real time.
            Find +EV bets, lock in arbitrage profits, and convert promotions
            into guaranteed cash.
          </p>
          <div className="landing-hero-actions">
            <button className="landing-cta-primary" onClick={onLoginRequest}>
              Get started free
            </button>
            <button className="landing-cta-secondary" onClick={onLoginRequest}>
              Log in
            </button>
          </div>
          <div className="landing-books-strip">
            <div className="landing-books-track">
              {[...BOOKS, ...BOOKS].map((book, i) => (
                <span
                  key={i}
                  className="landing-book-pill"
                  style={{ color: book.color, background: book.bg, borderColor: book.border }}
                >
                  {book.name}
                </span>
              ))}
            </div>
          </div>
          <p className="landing-kalshi-soon">+ Kalshi coming soon</p>
        </div>
      </section>


      {/* ── Live Preview ── */}
      {hasPreview && (
        <section className="landing-preview">
          <div className="landing-section-inner">
            <h2 className="landing-section-heading">What's on the board right now</h2>
            <p className="landing-section-sub">
              Real arbitrage opportunities and live odds pulled from every major sportsbook.
            </p>
            <div className="landing-preview-grid">
              {stats.sampleArb && (
                <div className="landing-preview-card-col">
                  <div className="landing-preview-card-label">Arbitrage opportunity</div>
                  <PreviewArbCard arb={stats.sampleArb} onLoginRequest={onLoginRequest} />
                </div>
              )}
              {stats.sampleGame && (
                <div className="landing-preview-card-col">
                  <div className="landing-preview-card-label">Live odds comparison</div>
                  <PreviewOddsCard game={stats.sampleGame} onLoginRequest={onLoginRequest} />
                </div>
              )}
            </div>
            <div className="landing-preview-cta-row">
              <button className="landing-cta-primary" onClick={onLoginRequest}>
                Sign up free to find arbitrage opportunities, +EV bets, and turn promos into guaranteed cash →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Tools ── */}
      <section className="landing-tools">
        <div className="landing-section-inner">
          <h2 className="landing-section-heading anim">Everything you need to bet smarter</h2>
          <p className="landing-section-sub anim anim-delay-1">
            Four tools built around proven strategies used by professional bettors.
          </p>
          <div className="landing-tools-grid">
            {TOOLS.map((tool, i) => {
              const Icon = tool.icon;
              return (
                <div key={tool.className} className={`landing-tool-card anim anim-delay-${i + 1}`}>
                  <div className="landing-tool-card-header">
                    <div className={`landing-tool-icon landing-tool-icon-${tool.className}`}>
                      <Icon size={19} />
                    </div>
                    <div>
                      <div className={`landing-tool-title landing-tool-title-${tool.className}`}>
                        {tool.title}
                      </div>
                      <div className="landing-tool-tagline">{tool.tagline}</div>
                    </div>
                  </div>
                  <p className="landing-tool-desc">{tool.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ── How it works ── */}
      <section className="landing-how">
        <div className="landing-section-inner">
          <h2 className="landing-section-heading anim">How it works</h2>
          <p className="landing-section-sub anim anim-delay-1">Three steps, automated.</p>
          <div className="landing-steps">
            {STEPS.map((step, i) => (
              <div key={step.number} className={`landing-step anim anim-delay-${i + 1}`}>
                {i < STEPS.length - 1 && <div className="landing-step-connector" />}
                <div className="landing-step-num">{step.number}</div>
                <h3 className="landing-step-title">{step.title}</h3>
                <p className="landing-step-body">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Results ── */}
      <section className="landing-results">
        <div className="landing-section-inner">
          <h2 className="landing-section-heading">Real results. No luck required.</h2>
          <p className="landing-section-sub">Real numbers from a real account. $2,000 starting bankroll. 30 days.</p>
          <div className="landing-results-card">
            <div className="landing-results-headline">
              <span className="landing-results-profit">+$503</span>
              <span className="landing-results-return">25% return in 30 days</span>
            </div>
            <div className="landing-results-divider" />
            <div className="landing-results-tools">
              <div className="landing-results-tool">
                <div className="landing-results-tool-name">Arbitrage</div>
                <div className="landing-results-tool-profit">+$110</div>
                <div className="landing-results-tool-stats">14 bets · 100% wins</div>
              </div>
              <div className="landing-results-tool-sep" />
              <div className="landing-results-tool">
                <div className="landing-results-tool-name">Promo Converter</div>
                <div className="landing-results-tool-profit">+$369</div>
                <div className="landing-results-tool-stats">96 conversions · 74% win rate</div>
              </div>
              <div className="landing-results-tool-sep" />
              <div className="landing-results-tool">
                <div className="landing-results-tool-name">+EV Finder</div>
                <div className="landing-results-tool-profit">+$24</div>
                <div className="landing-results-tool-stats">2 bets · 100% wins</div>
              </div>
            </div>
            <p className="landing-results-disclaimer">Results from May–June 2026. Individual results vary.</p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="landing-faq">
        <div className="landing-section-inner">
          <h2 className="landing-section-heading">Common questions</h2>
          <p className="landing-section-sub">
            Everything you're probably wondering before you sign up.
          </p>
          <div className="faq-list">
            {FAQS.map((faq, i) => <FaqItem key={i} faq={faq} />)}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="landing-cta-block">
        <div className="landing-section-inner landing-cta-block-inner">
          <h2 className="landing-cta-block-title anim">Start finding edges today</h2>
          <p className="landing-cta-block-sub anim anim-delay-1">Free to use. No credit card required.</p>
          <button className="landing-cta-primary landing-cta-primary--lg" onClick={onLoginRequest}>
            Create free account
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-section-inner">
          <div className="landing-footer-top">
            <img src="/navbets-logo-new.png" alt="NavBets" className="landing-logo landing-logo--footer" />
            <nav className="landing-footer-nav">
              {['Odds Dashboard', '+EV Finder', 'Arbitrage', 'Promo Converter', 'My Bets'].map(name => (
                <button key={name} className="landing-footer-link" onClick={onLoginRequest}>
                  {name}
                </button>
              ))}
              <button className="landing-footer-link" onClick={onContact}>Contact Us</button>
            </nav>
          </div>
          <p className="landing-footer-disclaimer">
            NavBets is for informational purposes only. We are not responsible for any financial
            losses, account restrictions, or limitations imposed by sportsbooks. Betting involves
            risk — only wager what you can afford to lose. Please gamble responsibly.
          </p>
        </div>
      </footer>

    </div>
  );
}

export default LandingPage;
